/* mcCatalog.js — the Module Configurator's TECHNICIAN-DRIVEN option library
 * (2026-07-20 user mandate: "Module Configurator needs to come from cabinet
 * technician too"). Builds the exact MC_LIBRARY shape the mcv3 panel renders,
 * but every option comes from the tenant catalog blob (Material Library ∪ the
 * placed product's Products-page allow-lists):
 *   carcass ← blob.colors        door ← blob.groups (+doors as colours)
 *   handle  ← blob.handles       drawer ← blob.equipment.drawers
 *   plinth  ← blob.pmat          lighting ← blob.lighting types for the unit
 * Sections the catalog can't fill fall back to the static MC_LIBRARY — the
 * panel never renders empty. Pure function; gating helpers come from the
 * catalog lib (productForType + allow-list indexes). */
import { MC_LIBRARY } from "./mcLibrary";
import { tokFor } from "../../engine/catalogIds";
import { productForType } from "../../../catalog/lib/wizardBridge";
import { allowedCarcIdx, allowedGroupIdx, allowedGolaIdx, allowedPlinthIdx, compAllowedIdx, optAllows, osOptApplies, handlePlacementsOf, placementAllows } from "../../../catalog/lib/pricing";
import { lightingTypesFor } from "../../../catalog/lib/units";
import { fabricOptionsFor, hasFabrics } from "../../engine/fabrics";

const FINISH_HEX = [
  [/brass|gold/i, "#B8915A"], [/black|nero|anthra/i, "#1B1B1D"], [/champagne/i, "#C5B59A"],
  [/chrome/i, "#D8D8DA"], [/white|bianco/i, "#F4F4F0"], [/bronze/i, "#7A5D3A"],
];
const finishHex = (s) => { for (const [re, hex] of FINISH_HEX) { if (re.test(String(s || ""))) return hex; } return "#B8BCC0"; };
const sw = (c) => (c && c.sw) || "#9A9BA0";

/** mcLibraryFromBlob(blob, sku) → MC_LIBRARY-shaped object from the catalog. */
export function mcLibraryFromBlob(blob, sku) {
  if (!blob || !Array.isArray(blob.colors)) return MC_LIBRARY;
  const unit = productForType(blob, sku) || null;
  const gate = { carc: null, grp: null, gola: null, plinth: null, handles: null, drawers: null };
  try {
    if (unit) {
      gate.carc = new Set(allowedCarcIdx(blob, unit));
      gate.grp = new Set(allowedGroupIdx(blob, unit));
      gate.plinth = new Set(allowedPlinthIdx(blob, unit));
      gate.handles = new Set(compAllowedIdx(blob, unit, "handles"));
      gate.drawers = new Set(compAllowedIdx(blob, unit, "drawers"));
    }
  } catch (e) { /* ungated when helpers reject the unit */ }
  // own try: allowedGolaIdx walks blob.gola directly — a blob without the
  // array must not knock out the OTHER gates (it did, once)
  try { if (unit && Array.isArray(blob.gola)) gate.gola = new Set(allowedGolaIdx(blob, unit)); } catch (e) { /* ungated */ }
  const allowed = (set, i) => (set == null ? true : set.has(i));

  const carcassColors = (blob.colors || [])
    .map((c, i) => ({ i, c })).filter(({ i }) => allowed(gate.carc, i))
    // tex rides along so the panel tile shows the ACTUAL finish photo (2026-08-15
    // "carcass colour fully connected"), not the grey hex fallback
    .map(({ i, c }) => ({ id: "carc-" + tokFor(c, i), label: c.name, hex: sw(c), tex: c.tex || null }));

  const doorTypes = [];
  const colorsByType = {};
  (blob.groups || []).forEach((g, i) => {
    if (!allowed(gate.grp, i)) return;
    const id = "pg" + g.g;
    doorTypes.push({ id, label: g.name || ("PG" + g.g), desc: (g.finish || "Front group") + " · PG" + g.g });
    colorsByType[id] = (g.doors || []).map((d, k) => ({ id: id + "-c" + tokFor(d, k), label: d.name || "Front", hex: d.sw || "#9A9BA0", tex: d.tex || null }));
  });

  // HANDLES: one SHARED colour pool for ALL handles (2026-08-10 user mandate:
  // "one Handle Colors section — those colors become available for all
  // handles"; blob.handleColors, technician Handles page). Each handle keeps
  // its OWN 3D model + its OWN admin-authored PLACEMENTS (exact-mm positions
  // the customer may choose between — never free positioning).
  const sharedHandleColors = (blob.handleColors || [])
    .map((c, k) => ({ id: "hc-" + tokFor(c, k), label: (c && c.name) || ("Finish " + (k + 1)), hex: (c && c.sw) || finishHex((c && c.name) || ""), price: (c && c.price) || 0 }));
  const handleTypes = (blob.handles || [])
    .map((h, i) => ({ i, h })).filter(({ i }) => allowed(gate.handles, i))
    .map(({ h }) => ({
      id: h.code, label: h.name || h.code,
      model: h.model || null,
      // positions: the STANDARD Top/Center/Bottom trio unless the technician
      // authored custom ones (handlePlacementsOf), GATED by the module's
      // allowed-position checks (bench → u.opts.handlePlacements)
      placements: handlePlacementsOf(h)
        // gated PER HANDLE (2026-08-15): each handle carries its own list of
        // allowed positions, so one handle's checks never restrict another's
        .filter((pl) => !unit || placementAllows(unit, h.code, pl.id)),
      colors: sharedHandleColors.length
        ? sharedHandleColors
        // no pool yet: the handle's single `finish` string as before
        : ((h.finish || "").trim() ? [{ id: h.code + "-c0", label: h.finish.trim(), hex: finishHex(h.finish) }] : []),
    }));
  // per-handle map (same shared list per type) + the flat list (legacy path)
  const handleColorsByType = {};
  handleTypes.forEach((t) => { handleColorsByType[t.id] = t.colors; });
  const handleColors = sharedHandleColors.slice();
  const seenFin = new Set(sharedHandleColors.map((c) => c.label.toLowerCase()));
  (blob.handles || []).forEach((h, i) => {
    if (!allowed(gate.handles, i)) return;   // finishes of disallowed handles stay hidden
    const f = (h.finish || "").trim();
    if (!f || seenFin.has(f.toLowerCase())) return;
    seenFin.add(f.toLowerCase());
    handleColors.push({ id: "hf-" + seenFin.size, label: f, hex: finishHex(f) });
  });

  const runners = ((blob.equipment || {}).drawers || [])
    .map((d, i) => ({ i, d })).filter(({ i }) => allowed(gate.drawers, i))
    .map(({ d }) => ({ id: d.code, label: (d.brand ? d.brand + " " : "") + (d.model || d.code), desc: d.spec || "" }));
  const runnerColors = [];
  const seenRF = new Set();
  ((blob.equipment || {}).drawers || []).forEach((d, i) => {
    if (!allowed(gate.drawers, i)) return;   // finishes of disallowed runners stay hidden
    const f = (d.finish || "").trim();
    if (!f || seenRF.has(f.toLowerCase())) return;
    seenRF.add(f.toLowerCase());
    runnerColors.push({ id: "drf-" + seenRF.size, label: f, hex: finishHex(f) });
  });

  const pmatList = (blob.pmat || [])
    .map((m, i) => ({ i, m })).filter(({ i }) => allowed(gate.plinth, i))
    .map(({ m, i }) => ({ id: m.key || ("plinth-" + tokFor(m, i)), label: m.name }));
  const plinthColors = [];
  (blob.pmat || []).forEach((m, mi) => {
    if (!allowed(gate.plinth, mi)) return;   // colours of disallowed materials stay hidden
    (m.colors || []).forEach((c, ci) => {
      plinthColors.push({ id: "pmc-" + tokFor(m, mi) + "-" + tokFor(c, ci), label: c.name, hex: sw(c), tex: c.tex || null });
    });
  });

  // worktops (per-module worktop choice, hybrid-modular §8/§16): technician
  // MatLib entries, allow-list gated by NAME (optset cat "worktops"); the
  // price shown in the panel is the SAME delta the pricing engines charge.
  const worktops = (blob.worktops || [])
    .filter((w) => w && w.name && (!unit || optAllows(unit, "worktops", w.name)))
    .map((w) => ({
      id: w.name, label: w.name, price: Number(w.price) || 0,
      hex: (w.colors && w.colors[0] && w.colors[0].sw) || null,
    }));

  // GOLA (2026-08-10): the technician's Gola page is its own allowed-options
  // column, so it is its own tab — ids stay INDEX-based ("gola-K", the
  // convention the rest of the app already stores).
  const golaOpts = (blob.gola || [])
    .map((g, i) => ({ i, g })).filter(({ i }) => allowed(gate.gola, i))
    .map(({ g, i }) => ({
      id: "gola-" + tokFor(g, i), label: g.name || ("Gola " + (i + 1)),
      hex: g.sw || finishHex(g.finish || g.name || ""), tex: g.tex || null,
      price: Number(g.price) || 0,
    }));
  // GOLA COLOURS (2026-08-15): ONE shared pool for ALL profiles (technician
  // Gola page → blob.golaColors), same contract as the handle colour pool.
  const golaColors = (blob.golaColors || [])
    .map((c, k) => ({
      id: "gc-" + tokFor(c, k), label: (c && c.name) || ("Finish " + (k + 1)),
      hex: (c && c.sw) || finishHex((c && c.name) || ""), price: (c && c.price) || 0,
    }));
  // SIDE PANELS (2026-08-29): decorative end panels — same id contract as
  // gola ("sp-" + uid token); offered for every module (v1: ungated).
  const sidePanelOpts = (blob.sidePanels || [])
    .map((g, i2) => ({ i: i2, g })).filter(({ g }) => g)
    .map(({ i: i2, g }) => ({
      id: "sp-" + tokFor(g, i2), label: g.name || ("Panel " + (i2 + 1)),
      sw: g.sw || null, tex: g.tex || null, price: g.price || 0,
    }));


  // BACKSPLASH (2026-08-10): MatList entries, NAME-keyed and allow-list gated
  // exactly like worktops — the same delta both pricing engines charge.
  const backsplash = (blob.backsplash || [])
    .filter((b) => b && b.name && (!unit || optAllows(unit, "backsplash", b.name)))
    .map((b) => ({
      id: b.name, label: b.name, price: Number(b.price) || 0,
      hex: (b.colors && b.colors[0] && b.colors[0].sw) || null,
    }));

  // interior accessories (Phase 1): the technician's equipment library —
  // per-category allow-lists (optAllows(u, cat, code)) + the same v4.56
  // applicability gates the technician table uses (osOptApplies). The price
  // shown IS the delta both pricing engines charge per item.
  const INT_CATS = [
    ["shelves", "Internal shelves"], ["organizers", "Drawer organizers"], ["bins", "Bins"],
    ["corners", "Corner units"], ["pullouts", "Pull-outs"], ["columns", "Column internals"],
  ];
  const interiorCats = [];
  INT_CATS.forEach(([cat, label]) => {
    if (unit) { try { if (!osOptApplies(unit, cat)) return; } catch (e) { /* ungated */ } }
    const items = (((blob.equipment || {})[cat]) || [])
      .filter((it) => it && it.code && (!unit || optAllows(unit, cat, it.code)))
      .map((it) => ({
        code: it.code,
        label: (it.brand ? it.brand + " " : "") + (it.model || it.code),
        spec: it.spec || "", price: Number(it.price) || 0,
      }));
    if (items.length) interiorCats.push({ cat, label, items });
  });

  // EQUIPMENT (2026-08-18 user: "taps hobs ovens hoods fridges dishwashers —
  // add it in Edit Module as Equipment, put it below Plinth"): the appliance
  // categories of the technician's Equipment Library, gated by the product's
  // "equipment" allow-list (the bench's own gate). Each item carries its .glb
  // so the cabinet renders the real appliance in real time.
  const EQ_CATS = [
    ["sinks", "eqSinks", "Sink"],                                     // batch 11: sinks render in 3D too
    ["taps", "eqTaps", "Tap"], ["hobs", "eqHobs", "Hob"], ["ovens", "eqOvens", "Oven"],
    ["hoods", "eqHoods", "Hood"], ["fridges", "eqFridges", "Fridge"], ["dishwashers", "eqDishwashers", "Dishwasher"],
  ];
  const equipCats = [];
  EQ_CATS.forEach(([cat, field, label]) => {
    if (unit && !optAllows(unit, "equipment", cat)) return;
    const items = (((blob.equipment || {})[cat]) || [])
      .filter((it) => it && it.code)
      .map((it) => ({
        id: it.code,
        label: (it.brand ? it.brand + " " : "") + (it.model || it.code),
        spec: it.spec || "", price: Number(it.price) || 0, glb: it.glb || null,
      }));
    if (items.length) equipCats.push({ cat, field, label, items });
  });

  const ltn = { drawer: "Drawer lighting", vertical: "Vertical lighting", horizontal: "Horizontal lighting" };
  const lightTypes = [{ id: "l-none", label: "No lighting" }];
  try {
    const u = unit || { sku: sku, cat: "" };
    lightingTypesFor(u).forEach((t) => {
      if (!unit || optAllows(unit, "lighting", t)) lightTypes.push({ id: t, label: ltn[t] || t });
    });
  } catch (e) { /* lighting stays none-only */ }

  /* 2026-07-23 single-source-of-truth mandate §14: with a REAL catalog blob,
   * the panel renders EXACTLY what the technician published — an empty section
   * stays empty (the technician removed those options) instead of silently
   * showing the static vocabulary. MC_LIBRARY remains ONLY the no-blob path
   * (catalog fetch failed / not loaded), surfaced via eng.catalogError.
   * Lighting temperatures are app configuration (not catalog entities). */
  return {
    carcass: { colors: carcassColors },
    door: { types: doorTypes, colorsByType },
    handle: { types: handleTypes, colors: handleColors, colorsByType: handleColorsByType },
    plinth: { materials: pmatList, colors: plinthColors },
    lighting: { types: lightTypes, temps: MC_LIBRARY.lighting.temps },
    drawer: { runners, colors: runnerColors },
    worktop: { options: worktops },
    gola: { options: golaOpts, colors: golaColors },
    sidePanel: {
      options: sidePanelOpts,
      // shared colour pool (2026-08-29d) — the Gola Colours contract
      colors: (blob.sidePanelColors || []).map((c, i2) => ({
        id: "spc-" + tokFor(c, i2), label: c.name || ("Colour " + (i2 + 1)),
        hex: c.sw || "#b9a58c", price: c.price || 0,
      })),
    },
    backsplash: { options: backsplash },
    interior: { cats: interiorCats },
    equip: { cats: equipCats },
    // batch 14: the Fabric Library for Indoor / Outdoor products (allow-list gated per group)
    fabric: { zones: unit && hasFabrics(unit) ? fabricOptionsFor(blob, unit) : [] },
  };
}
